import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { type User, login as loginApi, getMe, logout as logoutApi } from '../api/auth.api';
import { syncRatesToLocal } from '../utils/syncRates';
import { syncContractsToLocal } from '../utils/syncContracts';
import { syncMovementsToLocal } from '../utils/syncMovements';
import { reconcileSede } from '../utils/sede';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Vuelve a leer el usuario (p. ej. después de crear o editar sedes) */
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Verificar autenticación al cargar la app (la cookie se envía automáticamente)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const userData = await getMe();
        reconcileSede(userData.sedes, userData.rol === 'OPERADOR' ? userData.sedeId : null);
        setUser(userData);
        // Al recargar (p. ej. tras cambiar de sede) la caché offline toma las tarifas de la sede activa
        if (userData.company) {
          syncRatesToLocal(userData.company.id).catch(() => undefined);
        }
        
        // Mantener compatibilidad con localStorage para otros componentes
        localStorage.setItem('userRole', userData.rol);
        localStorage.setItem('userName', userData.nombre);
        localStorage.setItem('userEmail', userData.email);
        localStorage.setItem('user', JSON.stringify({ 
          companyId: userData.company?.id,
          rol: userData.rol,
          nombre: userData.nombre,
          email: userData.email
        }));
        if (userData.company) {
          localStorage.setItem('empresaId', userData.company.id);
          localStorage.setItem('empresaNombre', userData.company.nombre);
        }
      } catch (error: any) {
        console.error('Error en checkAuth:', error);
        
        // Solo cerrar sesión si es error de autenticación explícito (401/403)
        if (error.response?.status === 401 || error.response?.status === 403) {
          setUser(null);
          localStorage.clear(); // Limpiar todo
        } else {
          // Si es error de red, intentar restaurar desde localStorage
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            try {
              const parsedUser = JSON.parse(storedUser);
              console.warn('⚠️ No se pudo verificar sesión con servidor, usando datos locales');
              // Crear un user parcial desde localStorage
              setUser({
                id: 'offline',
                email: parsedUser.email || '',
                nombre: parsedUser.nombre || 'Usuario',
                rol: parsedUser.rol || 'OPERADOR',
                company: parsedUser.companyId ? { 
                  id: parsedUser.companyId,
                  nombre: localStorage.getItem('empresaNombre') || 'Empresa'
                } as any : undefined
              } as any);
            } catch (e) {
              console.error('Error parseando usuario de localStorage:', e);
              setUser(null);
            }
          } else {
            setUser(null);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string) => {
    const response = await loginApi({ email, password });
    
    // El token se guarda automáticamente en la cookie HttpOnly
    reconcileSede(response.user.sedes, response.user.rol === 'OPERADOR' ? response.user.sedeId : null);
    setUser(response.user);
    
    // Compatibilidad con localStorage para otros componentes
    localStorage.setItem('userRole', response.user.rol);
    localStorage.setItem('userName', response.user.nombre);
    localStorage.setItem('userEmail', response.user.email);
    // Guardar objeto completo para recuperación offline total
    localStorage.setItem('user', JSON.stringify({
      id: response.user.id,
      nombre: response.user.nombre,
      email: response.user.email,
      rol: response.user.rol,
      companyId: response.user.company?.id,
      company: response.user.company
    }));
    if (response.user.company) {
      localStorage.setItem('empresaId', response.user.company.id);
      localStorage.setItem('empresaNombre', response.user.company.nombre);
      
      // Sincronizar tarifas, contratos y movimientos al iniciar sesión
      Promise.all([
        syncRatesToLocal(response.user.company.id),
        syncContractsToLocal(response.user.company.id),
        syncMovementsToLocal(response.user.company.id)
      ]).catch(err => 
        console.warn('Error sincronizando datos offline:', err)
      );
    }
  };

  const refreshUser = async () => {
    const userData = await getMe();
    reconcileSede(userData.sedes, userData.rol === 'OPERADOR' ? userData.sedeId : null);
    setUser(userData);
  };

  const logout = async () => {
    await logoutApi();
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
